import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { MdClose } from "react-icons/md"

export default function ChipInput({
    label,
    name,
    placeholder,
    register,
    errors,
    setValue,
    getValues,
}) {
    const { editCourse, course } = useSelector((state) => state.course)

    // Setting up state for managing chips array and input value
    const [chips, setChips] = useState([])
    const [inputValue, setInputValue] = useState('')

    useEffect(() => {
        if (editCourse) {
            // console.log(course)
            setChips(course?.tag)
        }
        register(name, { required: true, validate: (value) => value.length > 0 })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setValue(name, chips)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chips])

    // Function to handle user input when chips are added
    const handleKeyDown = (event) => {
        // Check if user presses "Enter" or ","
        if (event.key === "Enter" || event.key === ",") {
            // Prevent the default behavior of the event
            event.preventDefault()
            // Get the input value and remove any leading/trailing spaces
            const chipValue = event.target.value.trim()
            // Check if the input value exists and is not already in the chips array
            if (chipValue && !chips.includes(chipValue)) {
                // Add the chip to the array and clear the input
                const newChips = [...chips, chipValue]
                setChips(newChips)
                setInputValue('') // Clear the input value
            }
        }
    }

    // Function to handle deletion of a chip
    const handleDeleteChip = (chipIndex) => {
        // Filter the chips array to remove the chip with the given index
        const newChips = chips.filter((_, index) => index !== chipIndex)
        setChips(newChips)
    }

    // Function to handle change in input value
    const handleChange = (event) => {
        setInputValue(event.target.value)
    }

    // Function to handle addition of requirement
    const handleAddRequirement = () => {
        const chipValue = inputValue.trim()
        if (chipValue && !chips.includes(chipValue)) {
            const newChips = [...chips, chipValue]
            setChips(newChips)
            setInputValue('')
        }
    }

    // Render the component
    return (
        <div className="flex flex-col space-y-2">
            {/* Render the label for the input */}
            <label className="text-sm text-richblack-5" htmlFor={name}>
                {label} <sup className="text-pink-200">*</sup>
            </label>
            {/* Render the chips and input */}
            <div className="flex w-full flex-wrap gap-y-2">
                {/* Map over the chips array and render each chip */}
                {chips.map((chip, index) => (
                    <div
                        key={index}
                        className="m-1 flex items-center rounded-full bg-yellow-400 px-2 py-1 text-sm text-richblack-5"
                    >
                        {/* Render the chip value */}
                        {chip}
                        {/* Render the button to delete the chip */}
                        <button
                            type="button"
                            className="ml-2 focus:outline-none"
                            onClick={() => handleDeleteChip(index)}
                        >
                            <MdClose className="text-sm" />
                        </button>
                    </div>
                ))}
                {/* Render the input for adding new chips */}
                <input
                    id={name}
                    name={name}
                    type="text"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    className="w-full text-richblack-100 bg-richblack-700 h-[2rem] md:h-[3rem] rounded-md px-3 shadow-sm shadow-richblack-200 focus:outline-none focus:bg-richblack-700"
                />
                {/* Conditionally render the Add button */}
                {inputValue && (
                    <button
                        type="button"
                        onClick={handleAddRequirement}
                        className="font-semibold text-yellow-50"
                    >
                        Add
                    </button>
                )}
            </div>
            {/* Render an error message if the input is required and not filled */}
            {errors[name] && (
                <span className="ml-2 text-xs tracking-wide text-pink-200">
                    {label} is required
                </span>
            )}
        </div>
    )
}